import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Bike,
  CalendarClock,
  ExternalLink,
  MessageSquare,
  Package,
  Printer,
  Search,
  Store,
} from 'lucide-react';
import {
  DUE_BUCKET_LABELS,
  formatMoney,
  formatShopDate,
  formatShopTime,
  relativeTime,
  type DueBucket,
  type OrderStatus,
  type OrderWithDetail,
} from '@bakery/shared';

import { api, type NotifyResult } from '../lib/api.js';
import { printReceipt } from '../lib/printing.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Modal,
  Select,
  Skeleton,
  Tabs,
  Textarea,
  cx,
  useToast,
} from '../components/ui.js';

const BUCKETS: DueBucket[] = ['overdue', 'today', 'tomorrow', 'advance', 'undated'];

const FULFILLMENT_ICON = {
  pickup: Store,
  delivery: Bike,
  dine_in: Store,
  courier: Package,
} as const;

export function OrdersScreen() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [bucket, setBucket] = useState<DueBucket | 'all'>('today');
  const [search, setSearch] = useState('');

  const statuses = useQuery({ queryKey: ['order-statuses'], queryFn: () => api.orders.statuses() });
  const buckets = useQuery({ queryKey: ['orders', 'buckets'], queryFn: api.orders.buckets, refetchInterval: 60_000 });
  const orders = useQuery({
    queryKey: ['orders', bucket, search],
    queryFn: () => api.orders.list({ bucket: bucket === 'all' ? undefined : bucket, search: search || undefined }),
    refetchInterval: 60_000,
  });

  const countFor = (value: DueBucket) => buckets.data?.find((b) => b.bucket === value)?.count ?? 0;

  return (
    <div className="mx-auto max-w-5xl px-4 py-5">
      <header className="mb-4">
        <h1 className="font-display text-2xl font-semibold">Orders</h1>
        <p className="mt-0.5 text-sm text-[var(--color-ink-soft)]">
          Grouped by when the customer needs them, not by when they were placed.
        </p>
      </header>

      <div className="mb-4 space-y-3">
        <Tabs
          value={bucket}
          onChange={setBucket}
          tabs={[
            ...BUCKETS.filter((value) => value !== 'undated' || countFor('undated') > 0).map((value) => ({
              value,
              label: DUE_BUCKET_LABELS[value],
              count: countFor(value),
              tone: value === 'overdue' ? ('danger' as const) : undefined,
            })),
            { value: 'all' as const, label: 'All open' },
          ]}
        />

        <div className="relative">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Order number, customer name or phone…"
            className="pl-9"
          />
        </div>
      </div>

      {orders.isError && <ErrorNote error={orders.error} retry={() => orders.refetch()} />}

      {orders.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} className="h-24" />
          ))}
        </div>
      ) : orders.data?.length === 0 ? (
        <EmptyState
          icon={<CalendarClock className="size-8" />}
          title={bucket === 'today' ? 'Nothing due today' : 'No orders here'}
          description={
            bucket === 'today'
              ? 'Orders due today will appear here as they come in from the register or the inbox.'
              : 'Try another tab, or clear the search.'
          }
        />
      ) : (
        <ul className="space-y-2">
          {orders.data?.map((order) => (
            <OrderRow key={order.id} order={order} onOpen={() => navigate(`/orders/${order.id}`)} />
          ))}
        </ul>
      )}

      {orderId && (
        <OrderDetail
          orderId={orderId}
          statuses={statuses.data ?? []}
          onClose={() => navigate('/orders')}
        />
      )}
    </div>
  );
}

function OrderRow({ order, onOpen }: { order: OrderWithDetail; onOpen: () => void }) {
  const Icon = FULFILLMENT_ICON[order.fulfillment] ?? Store;
  const isLate = order.dueBucket === 'overdue';

  return (
    <li>
      <button
        onClick={onOpen}
        className={cx(
          'card flex w-full items-center gap-4 p-4 text-left transition hover:border-crust-400 hover:elevation-raised',
          isLate && 'border-[var(--color-danger)]/40',
        )}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="tabular text-sm font-semibold">{order.orderNo}</span>
            <Badge color={order.status.color}>{order.status.name}</Badge>
            {isLate && (
              <Badge tone="danger">
                <AlertTriangle className="size-3" /> Late
              </Badge>
            )}
            {order.balanceDue > 0 && <Badge tone="warn">{formatMoney(order.balanceDue)} owed</Badge>}
          </div>

          <p className="mt-1 truncate text-sm text-[var(--color-ink)]">
            {order.customerDisplayName ?? 'Walk-in customer'}
          </p>

          <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[var(--color-ink-soft)]">
            <span className="inline-flex items-center gap-1">
              <Icon className="size-3.5" />
              {order.fulfillment === 'delivery' ? 'Delivery' : 'Pickup'}
            </span>
            {order.dueAt && (
              <span>
                {formatShopDate(order.dueAt)} · {formatShopTime(order.dueAt)}
              </span>
            )}
            <span className="truncate">
              {order.lines
                .slice(0, 2)
                .map((line) => `${line.qty}× ${line.nameSnapshot}`)
                .join(', ')}
              {order.lines.length > 2 && ` +${order.lines.length - 2} more`}
            </span>
          </p>
        </div>

        <span className="tabular shrink-0 font-display text-lg font-semibold">{formatMoney(order.total)}</span>
      </button>
    </li>
  );
}

function OrderDetail({
  orderId,
  statuses,
  onClose,
}: {
  orderId: string;
  statuses: OrderStatus[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [pendingStatus, setPendingStatus] = useState<OrderStatus | null>(null);

  const order = useQuery({ queryKey: ['order', orderId], queryFn: () => api.orders.get(orderId) });

  const print = useMutation({
    mutationFn: () => api.orders.markPrinted(orderId),
    onSuccess: async (receipt) => {
      await printReceipt(receipt);
      toast.push({ tone: 'ok', title: `Receipt ${receipt.receiptNo} sent to the printer` });
    },
  });

  function announce(result: NotifyResult | null) {
    if (!result) return;
    if (result.status === 'sent') {
      toast.push({ tone: 'ok', title: `Customer notified on ${result.channel}` });
    } else if (result.handoffUrl) {
      // The channel is not connected, but the message is written and ready.
      toast.push({
        tone: 'warn',
        title: 'Message ready to send',
        body: result.error ?? 'This channel needs finishing in its own app.',
        action: { label: `Open ${result.channel ?? 'app'} to send`, href: result.handoffUrl },
      });
    } else if (result.status === 'skipped') {
      toast.push({ tone: 'neutral', title: 'No message sent', body: result.error ?? undefined });
    } else {
      toast.push({ tone: 'danger', title: 'Message failed', body: result.error ?? undefined });
    }
  }

  if (order.isLoading) {
    return (
      <Modal open onClose={onClose} title="Loading order…">
        <Skeleton className="h-64" />
      </Modal>
    );
  }

  if (!order.data) {
    return (
      <Modal open onClose={onClose} title="Order not found">
        <p className="text-sm text-[var(--color-ink-soft)]">That order no longer exists.</p>
      </Modal>
    );
  }

  const data = order.data;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="lg"
        title={`${data.orderNo} — ${data.customerDisplayName ?? 'Walk-in'}`}
        description={
          data.dueAt
            ? `${data.fulfillment === 'delivery' ? 'Deliver' : 'Ready'} ${formatShopDate(data.dueAt)} at ${formatShopTime(data.dueAt)}`
            : 'No scheduled time'
        }
        footer={
          <>
            <Button variant="ghost" icon={<Printer className="size-4" />} loading={print.isPending} onClick={() => print.mutate()}>
              Receipt
            </Button>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </>
        }
      >
        <div className="space-y-5">
          {/* Status pipeline ------------------------------------------- */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">Move to</h3>
            <div className="flex flex-wrap gap-2">
              {statuses
                .filter((status) => status.id !== data.statusId)
                .map((status) => (
                  <button
                    key={status.id}
                    onClick={() => setPendingStatus(status)}
                    style={{ borderColor: `${status.color}66`, color: status.color }}
                    className="rounded-[var(--radius-md)] border-2 px-3 py-2 text-sm font-medium transition hover:bg-[var(--color-surface-sunken)]"
                  >
                    {status.name}
                  </button>
                ))}
            </div>
          </section>

          {/* Lines ------------------------------------------------------ */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">Items</h3>
            <Card className="divide-y divide-[var(--color-line)] overflow-hidden">
              {data.lines.map((line) => (
                <div key={line.id} className="flex items-center gap-3 px-4 py-2.5">
                  <span className="tabular w-8 text-sm font-medium">{line.qty}×</span>
                  <span className="min-w-0 flex-1 truncate text-sm">{line.nameSnapshot}</span>
                  <span className="tabular text-sm">{formatMoney(line.lineTotal)}</span>
                </div>
              ))}
              <div className="space-y-1 bg-[var(--color-surface-sunken)] px-4 py-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-ink-soft)]">Subtotal</span>
                  <span className="tabular">{formatMoney(data.subtotal)}</span>
                </div>
                {data.discountAmount > 0 && (
                  <div className="flex justify-between text-[var(--color-ok)]">
                    <span>Discount</span>
                    <span className="tabular">-{formatMoney(data.discountAmount)}</span>
                  </div>
                )}
                {data.deliveryFee > 0 && (
                  <div className="flex justify-between">
                    <span className="text-[var(--color-ink-soft)]">Delivery</span>
                    <span className="tabular">{formatMoney(data.deliveryFee)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-[var(--color-line)] pt-1.5 font-semibold">
                  <span>Total</span>
                  <span className="tabular">{formatMoney(data.total)}</span>
                </div>
                {data.balanceDue > 0 && (
                  <div className="flex justify-between font-semibold text-[var(--color-warn)]">
                    <span>Balance due</span>
                    <span className="tabular">{formatMoney(data.balanceDue)}</span>
                  </div>
                )}
              </div>
            </Card>
          </section>

          {(data.addressSnapshot || data.productionNotes || data.notes) && (
            <section className="space-y-2 text-sm">
              {data.addressSnapshot && (
                <p>
                  <span className="text-[var(--color-ink-soft)]">Address: </span>
                  {data.addressSnapshot}
                </p>
              )}
              {data.productionNotes && (
                <p>
                  <span className="text-[var(--color-ink-soft)]">For the baker: </span>
                  {data.productionNotes}
                </p>
              )}
              {data.notes && (
                <p>
                  <span className="text-[var(--color-ink-soft)]">Notes: </span>
                  {data.notes}
                </p>
              )}
            </section>
          )}

          {/* Timeline --------------------------------------------------- */}
          <section>
            <h3 className="mb-2 text-sm font-semibold">History</h3>
            <ol className="space-y-2">
              {data.events.map((event) => (
                <li key={event.id} className="flex gap-3 text-sm">
                  <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-crust-400" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[var(--color-ink)]">{event.note}</p>
                    <p className="text-xs text-[var(--color-ink-faint)]">
                      {relativeTime(event.createdAt)}
                      {event.notificationStatus && ` · message ${event.notificationStatus}`}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </Modal>

      {pendingStatus && (
        <StatusChangeModal
          order={data}
          status={pendingStatus}
          onClose={() => setPendingStatus(null)}
          onDone={(result) => {
            announce(result);
            setPendingStatus(null);
            queryClient.invalidateQueries({ queryKey: ['order', orderId] });
            queryClient.invalidateQueries({ queryKey: ['orders'] });
          }}
        />
      )}
    </>
  );
}

/**
 * Changing status is also the moment the customer hears from us, so the message
 * is shown — and editable — before it goes, rather than after.
 */
function StatusChangeModal({
  order,
  status,
  onClose,
  onDone,
}: {
  order: OrderWithDetail;
  status: OrderStatus;
  onClose: () => void;
  onDone: (result: NotifyResult | null) => void;
}) {
  const [notify, setNotify] = useState(status.autoNotify);
  const [body, setBody] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: ['order-preview', order.id, status.id],
    queryFn: () => api.orders.previewMessage(order.id, status.id),
  });

  const move = useMutation({
    mutationFn: () =>
      api.orders.changeStatus(order.id, {
        statusId: status.id,
        notify,
        bodyOverride: body ?? undefined,
      }),
    onSuccess: (result) => onDone(result.notification),
  });

  const text = body ?? preview.data?.preview ?? '';

  return (
    <Modal
      open
      onClose={onClose}
      title={`Move to ${status.name}`}
      description={status.description ?? undefined}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={move.isPending} onClick={() => move.mutate()}>
            {notify && preview.data?.hasTemplate ? 'Move and send' : 'Move'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {preview.data?.hasTemplate ? (
          <>
            <label className="flex items-center gap-2.5 text-sm">
              <input
                type="checkbox"
                checked={notify}
                onChange={(event) => setNotify(event.target.checked)}
                className="size-4 accent-[var(--color-crust-600)]"
              />
              <span className="font-medium">Send this message to {order.customerDisplayName ?? 'the customer'}</span>
            </label>

            {notify && (
              <Field label="Message" hint="Edit it if this order needs something different. The saved template is unchanged.">
                <Textarea
                  rows={10}
                  value={text}
                  onChange={(event) => setBody(event.target.value)}
                  className="font-mono text-[13px]"
                />
              </Field>
            )}
          </>
        ) : (
          <p className="text-sm text-[var(--color-ink-soft)]">
            No customer message is attached to “{status.name}”. You can add one in Settings → Order statuses.
          </p>
        )}
      </div>
    </Modal>
  );
}
